import React from 'react';
import MentionRenderer from './MentionRenderer';
import { renderMentionsAsComponents, processMentionHTML } from '../../utils/mentionUtils';
import { renderTextWithLinks } from '../../utils/contentConversion';

/**
 * Component to render content with mentions as interactive components
 * @param {Object} props - Component props
 * @param {string} props.content - Content with mention markup
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.isHTML - Whether content is HTML or plain text
 * @returns {JSX.Element} - Rendered content with mention components
 */
const ContentWithMentions = ({ 
  content, 
  className = '', 
  isHTML = false 
}) => {
  if (!content) {
    return null;
  }

  // Process content to extract mentions and text parts
  let contentParts;
  
  if (isHTML) {
    contentParts = processMentionHTML(content);
  } else {
    // For plain text content, check if it has mentions and render them
    if (content.includes('@[') && content.includes('](')) {
      contentParts = renderMentionsAsComponents(content, MentionRenderer);
    } else {
      // No mentions, return as plain content with URL detection
      return (
        <div 
          className={className}
          dangerouslySetInnerHTML={{ 
            __html: renderTextWithLinks(content) 
          }}
        />
      );
    }
  }

  // If no mentions found but we have content parts, render with URL detection
  if (contentParts.length === 1 && typeof contentParts[0] === 'string') {
    if (isHTML) {
      return (
        <div 
          className={className}
          dangerouslySetInnerHTML={{ __html: content }}
        />
      );
    } else {
      return (
        <div 
          className={className}
          dangerouslySetInnerHTML={{ 
            __html: renderTextWithLinks(contentParts[0]) 
          }}
        />
      );
    }
  }

  // Render content with mention components
  return (
    <div className={className}>
      {contentParts.map((part, index) => {
        if (typeof part === 'string') {
          // Regular text content with URL detection
          if (isHTML) {
            return (
              <span 
                key={index}
                dangerouslySetInnerHTML={{ __html: part }}
              />
            );
          }
          return (
            <span 
              key={index}
              dangerouslySetInnerHTML={{ 
                __html: renderTextWithLinks(part) 
              }}
            />
          );
        } else if (part.type === 'mention') {
          // Mention component
          return (
            <MentionRenderer
              key={part.key || `mention-${index}`}
              userId={part.userId}
              displayName={part.displayName}
              className="mx-1"
            />
          );
        }
        return null;
      })}
    </div>
  );
};

export default ContentWithMentions;